import { useEffect, useCallback, useRef, useState } from 'react';
import { useSelector, useDispatch, batch } from 'react-redux';
import { throttle } from '../utils/eventUtils';
import { NOW_PLAYING_TEMPLATE } from '../types/templates/nowPlaying';
import { VIDEO_NOW_PLAYING_TEMPLATE, } from '../types/templates/videoNowPlaying';
import { getCookie } from '../utils/getSkyfireHeaders';
import { SET_MSHOP_BOTTOM_OVERRIDE, ENABLE_MSHOP_BOTTOM_OVERRIDE } from '../actions';
export default function MShopHandler() {
    const dispatch = useDispatch();
    const { overlayTemplates } = useSelector((state) => state.TemplateStack);
    const media = useSelector((state) => state.Media);
    const throttledScrollEventListener = useCallback(throttle(scrollEventCallback, 100), []);
    const resizeObserver = useRef(new ResizeObserver(() => {
        throttledScrollEventListener();
    }));
    const rootElement = useRef(null);
    const [scrollEventListenerAdded, setScrollEventAdded] = useState(false);
    const [isGetBottomBarHeightAPIAvailable, setGetBottomBarHeightAPIAvailable] = useState(false);
    const [iosMShopBottomPadding, setIOSMShopBottomPadding] = useState(78);
    const hasOverlay = overlayTemplates.length > 0;
    const isAudioNPVShowing = hasOverlay &&
        !!overlayTemplates.find((template) => template.interface === NOW_PLAYING_TEMPLATE);
    const videoNPVTemplate = overlayTemplates.find((template) => template.interface === VIDEO_NOW_PLAYING_TEMPLATE);
    const isVideoNPVShowing = hasOverlay && !!videoNPVTemplate;
    const isNowPlaying = isAudioNPVShowing || isVideoNPVShowing;
    useEffect(() => {
        var _a;
        rootElement.current = document.getElementById('root');
        /**
         * Ref: https://w.amazon.com/bin/view/NavX/navx-core/BottomTabs/APIs/#H-1
         * Since the GetBottomBarHeight is available only in and above mShop version 21.11.0,
         * the fallback is to use the cookie approach already available.
         */
        (_a = window.P) === null || _a === void 0 ? void 0 : _a.when('ConfigChromeFramework').execute((ext) => {
            // @ts-ignore
            if (ext.getBottomBarsHeight) {
                // @ts-ignore
                ext.getBottomBarsHeight({
                    successCallback(result) {
                        setIOSMShopBottomPadding(Number(result.visibleHeight));
                        setGetBottomBarHeightAPIAvailable(true);
                    },
                    failCallback(result) {
                        setGetBottomBarHeightAPIAvailable(false);
                        console.log('FAILURE: Failed to get bottom bar height: ', result);
                    },
                });
            }
            else {
                setGetBottomBarHeightAPIAvailable(false);
            }
        });
        return () => {
            window.removeEventListener('scroll', throttledScrollEventListener);
            if (resizeObserver.current && rootElement.current) {
                resizeObserver.current.unobserve(rootElement.current);
            }
        };
    }, []);
    useEffect(() => {
        var _a;
        if (!isGetBottomBarHeightAPIAvailable) {
            const amznAppCookie = getCookie('amzn-app-ctxt');
            if (amznAppCookie) {
                try {
                    const decodedAppContext = decodeURIComponent(amznAppCookie);
                    if (/dm/.test(decodedAppContext) && decodedAppContext.indexOf('{') > -1) {
                        const AMZN_APP_CONTEXT = JSON.parse(decodedAppContext.slice(decodedAppContext.indexOf('{')));
                        /**
                         * https://w.amazon.com/bin/view/MASH/Application_Context_Protocol/1.0
                         * @{dm} - device metrics object
                         * @{pb} - padding bottom value of menubar at the bottom
                         */
                        const { pb = 0 } = (_a = AMZN_APP_CONTEXT === null || AMZN_APP_CONTEXT === void 0 ? void 0 : AMZN_APP_CONTEXT.dm) !== null && _a !== void 0 ? _a : {};
                        setIOSMShopBottomPadding(Number(pb));
                    }
                }
                catch (error) {
                    console.log(error);
                }
            }
        }
    }, [isGetBottomBarHeightAPIAvailable]);
    useEffect(() => {
        batch(() => {
            dispatch({
                type: ENABLE_MSHOP_BOTTOM_OVERRIDE,
                payload: { enableIOSMShopBottomOverride: true },
            });
            dispatch({
                type: SET_MSHOP_BOTTOM_OVERRIDE,
                payload: { iOSMShopBottomOverride: iosMShopBottomPadding },
            });
        });
    }, [iosMShopBottomPadding]);
    useEffect(() => {
        // Assign listeners only when playback started and transport component is visible
        if (media && (media === null || media === void 0 ? void 0 : media.mediaId) && !isNowPlaying && !scrollEventListenerAdded) {
            setScrollEventAdded(true);
            // scroll event listener to check whether we have reached end of page
            window.addEventListener('scroll', throttledScrollEventListener);
            // ResizeObserver to check for height modifications happening due to view engine
            if (window.ResizeObserver && rootElement.current) {
                resizeObserver.current.observe(rootElement.current);
            }
        }
    }, [isNowPlaying, media === null || media === void 0 ? void 0 : media.mediaId]);
    function scrollEventCallback() {
        // check whether we have reached the bottom of the page by calculating root element height
        // and vertical scroll
        if (rootElement.current &&
            window.innerHeight + window.scrollY >= rootElement.current.offsetHeight) {
            dispatch({
                type: ENABLE_MSHOP_BOTTOM_OVERRIDE,
                payload: { enableIOSMShopBottomOverride: false },
            });
        }
        else {
            dispatch({
                type: ENABLE_MSHOP_BOTTOM_OVERRIDE,
                payload: { enableIOSMShopBottomOverride: true },
            });
        }
    }
    return null;
}
